import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../apiServices/axios';
import { GeneralGPT } from '../apiServices/api_routes';

export const sendGeneralChatMessage = createAsyncThunk(
    'generalChat/sendGeneralChatMessage',
    async ({ openaiMessages, tempUserId, tempAssistantId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(GeneralGPT + 'chat/send/', {
                messages: openaiMessages,
            });
            return {
                data: response.data,
                tempUserId,
                tempAssistantId,
            };
        } catch (err) {
            const message = err?.response?.data?.error || err?.message || 'Failed to send message';
            return rejectWithValue(message);
        }
    }
);

const generalChatSlice = createSlice({
    name: 'generalChat',
    initialState: {
        messages: [],
        loading: false,
        error: null,
    },
    reducers: {
        addTempMessage: (state, { payload }) => {
            state.messages.push(payload);
        },
        removeMessageById: (state, { payload }) => {
            state.messages = state.messages.filter(m => m.id !== payload?.id);
        },
        clearGeneralChat: state => {
            state.messages = [];
            state.loading = false;
            state.error = null;
        },
        // Update a message's `is_temp` flag after it is confirmed by the backend.
        setMessageFinal: (state, { payload }) => {
            const idx = state.messages.findIndex(m => m.id === payload?.id);
            if (idx === -1) return;
            state.messages[idx] = {
                ...state.messages[idx],
                ...payload.updates,
                is_temp: false,
            };
        },
    },
    extraReducers: builder => {
        builder
            .addCase(sendGeneralChatMessage.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sendGeneralChatMessage.fulfilled, (state, { payload }) => {
                state.loading = false;

                const { data, tempUserId, tempAssistantId } = payload;
                const assistantText = data?.assistant_message?.content;

                // Remove temp assistant bubble (or any placeholder) and finalize temp user if present.
                state.messages = state.messages.filter(m => m.id !== tempAssistantId);

                if (tempUserId) {
                    state.messages = state.messages.map(m => {
                        if (m.id !== tempUserId) return m;
                        return { ...m, is_temp: false };
                    });
                }

                state.messages.push({
                    id: `ai-${Date.now()}`,
                    role: 'assistant',
                    content: assistantText || '',
                    created_at: new Date().toISOString(),
                    is_temp: false,
                    tokens_used: data?.tokens_used,
                    model_used: data?.model_used,
                });
            })
            .addCase(sendGeneralChatMessage.rejected, (state, action) => {
                state.loading = false;
                const { tempUserId, tempAssistantId } = action.meta?.arg || {};
                state.messages = state.messages.filter(m => m.id !== tempUserId && m.id !== tempAssistantId);
                state.error = action.payload || action.error?.message || 'Failed to send message';
            });
    },
});

export const { addTempMessage, removeMessageById, clearGeneralChat, setMessageFinal } = generalChatSlice.actions;
export default generalChatSlice.reducer;

